import { useState, useEffect } from "react";

import CustomButton from "components/common/Button";
import Text from "components/common/Text";
import CustomSnackbar, { AlertType } from "components/common/Snackbar";

import { sendReferralInvites, ReferralRockMember } from "services/referral";

import { hasPermission } from "hooks/usePermission";

import FacebookShareIcon from "assets/images/fb_share.svg";
import XShareIcon from "assets/images/x_share.svg";
import LinkedInShareIcon from "assets/images/linkedin_share.svg";
import WhatsAppShareIcon from "assets/images/whatsapp_share.svg";

import EmailPreviewModal from "./EmailPreviewModal";
import "./ReferBusiness.scss";

interface ReferBusinessProps {
  isEnrolled: boolean;
  memberData: ReferralRockMember | null;
}

function ReferBusiness({ isEnrolled, memberData }: ReferBusinessProps) {
  const canInvite = hasPermission("referrals.invite");
  const [referralLink, setReferralLink] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: AlertType.success });

  const emailMessage =
    "Hiring remote talent? Edge helps you build a reliable, cost-effective team with pre-vetted professionals and end-to-end support.";

  useEffect(() => {
    if (isEnrolled && memberData) {
      setReferralLink(memberData.referralURL || "");
    }
  }, [isEnrolled, memberData]);

  const handleCopyMessage = () => {
    const fullMessage = `${emailMessage} ${referralLink}`;
    navigator.clipboard.writeText(fullMessage);
    setSnackbar({ open: true, message: "Message copied to clipboard!", type: AlertType.success });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSocialShare = (platform: string) => {
    const fullMessage = `${emailMessage}\n\n${referralLink}`;
    const encodedMessage = encodeURIComponent(fullMessage);
    const encodedLink = encodeURIComponent(referralLink);

    let shareUrl = "";
    switch (platform) {
      case "linkedin":
        shareUrl = `https://www.linkedin.com/feed/?shareActive=true&text=${encodedMessage}`;
        break;
      case "facebook":
        navigator.clipboard.writeText(fullMessage);
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedLink}`;
        setSnackbar({ open: true, message: "Message copied! Paste it in the Facebook post.", type: AlertType.info });
        break;
      case "twitter":
        shareUrl = `https://twitter.com/intent/tweet?text=${encodedMessage}`;
        break;
      case "whatsapp":
        shareUrl = `https://wa.me/?text=${encodedMessage}`;
        break;
      default:
        return;
    }

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleSendInvites = async () => {
    if (!emailInput.trim()) {
      setSnackbar({ open: true, message: "Please enter at least one email address", type: AlertType.error });
      return;
    }

    const emails = emailInput
      .split(",")
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setSnackbar({ open: true, message: `Invalid email(s): ${invalidEmails.join(", ")}`, type: AlertType.error });
      return;
    }

    setIsLoading(true);
    try {
      const response = await sendReferralInvites(emails);
      if (response.ok) {
        setSnackbar({
          open: true,
          message: `Successfully sent ${response.payload.invitesSent} invite(s)!`,
          type: AlertType.success,
        });
        setEmailInput("");
      } else {
        setSnackbar({ open: true, message: "Failed to send invites. Please try again.", type: AlertType.error });
      }
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred. Please try again.", type: AlertType.error });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isEnrolled) {
    return null;
  }

  return (
    <div className="flex flex-col gap-075">
      <div>
        <h2 className="section-title">Share your referral link with others</h2>
        <div className="share-message-card">
          <div className="helper-text">Copy and send this message</div>
          <div className="message-content">
            {emailMessage}
            <br />
            <a href={referralLink} target="_blank" rel="noopener noreferrer" className="referral-link">
              {referralLink}
            </a>
          </div>
        </div>

        <div className="share-actions-row">
          <CustomButton
            label="Copy message"
            onClick={handleCopyMessage}
            variant="contained"
            color="secondary"
            size="large"
          />
          <div className="share-social-container">
            <span className="share-label">Share via</span>
            <div className="flex gap-05">
              <button
                className="social-icon-button"
                onClick={() => handleSocialShare("facebook")}
                aria-label="Share on Facebook"
                type="button"
              >
                <img src={FacebookShareIcon} alt="Facebook" />
              </button>
              <button
                className="social-icon-button"
                onClick={() => handleSocialShare("twitter")}
                aria-label="Share on Twitter"
                type="button"
              >
                <img src={XShareIcon} alt="X" />
              </button>
              <button
                className="social-icon-button"
                onClick={() => handleSocialShare("linkedin")}
                aria-label="Share on LinkedIn"
                type="button"
              >
                <img src={LinkedInShareIcon} alt="LinkedIn" />
              </button>
              <button
                className="social-icon-button"
                onClick={() => handleSocialShare("whatsapp")}
                aria-label="Share on WhatsApp"
                type="button"
              >
                <img src={WhatsAppShareIcon} alt="WhatsApp" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {canInvite && <div className="divider-line" />}

      {canInvite && (
        <div className="flex flex-col gap-075">
          <h2 className="section-title">Or refer businesses using their email</h2>
          <div className="email-input-container">
          <Text
            name="emailInput"
            type="text"
            placeholder="Add their email addresses"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            variant="outlined"
            helperTextWithoutError="Separate emails with commas"
            size="medium"
            label=""
            helperText=""
            sx={{}}
            isOptional={false}
            minValue={0}
            error={false}
            startIcon={null}
            endIcon={null}
            labelIcon={null}
            maxRows={1}
            minRows={1}
          />
        </div>

          <div className="email-actions-row">
            <span className="preview-email-link" onClick={() => setPreviewModalOpen(true)}>
              Preview email
            </span>
            <CustomButton
              label="Send invite"
              onClick={handleSendInvites}
              isDisabled={isLoading}
              isLoading={isLoading}
              variant="contained"
              color="primary"
              size="large"
            />
          </div>

          <EmailPreviewModal open={previewModalOpen} onClose={() => setPreviewModalOpen(false)} />
        </div>
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        type={snackbar.type}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
}

export default ReferBusiness;
